import type { Component } from "svelte";
import { loadPage } from "$lib/release/loadPage";

// One table of the admin console's lazily loaded pages. The shell renders from it, and
// anything that can predict a navigation (nav hover, a customer row under the pointer)
// warms the same chunk ahead of the click. A chunk that has already arrived renders
// synchronously, so a prefetched navigation never flashes the loading state.

// The page props differ per route; the shell passes exactly what each route takes.
type AnyPage = Component<any>;
type PageModule = { default: AnyPage };

const loaders = {
  "admin.entitlements": () => import("./AdminEntitlements.svelte"),
  "admin.customer": () => import("./AdminCustomerDetail.svelte"),
  "admin.customer.quote.new": () => import("./AdminQuoteComposer.svelte"),
  "admin.organizations": () => import("./AdminOrganizations.svelte"),
  "admin.revenue": () => import("./AdminRevenue.svelte"),
  "admin.audit": () => import("./AdminAudit.svelte"),
  "admin.discord": () => import("./AdminDiscord.svelte"),
  "admin.contracts": () => import("./AdminContracts.svelte"),
  "admin.contract.new": () => import("./AdminContractDetail.svelte"),
  "admin.contract": () => import("./AdminContractDetail.svelte"),
  "admin.contract-queue": () => import("./AdminContractQueue.svelte"),
  "admin.contract-queue.item": () => import("./AdminContractQueueDetail.svelte")
} satisfies Record<string, () => Promise<PageModule>>;

export type AdminPageName = keyof typeof loaders;

const ready = new Map<AdminPageName, AnyPage>();

export function isAdminPage(name: string): name is AdminPageName {
  return name in loaders;
}

/** The page component when its chunk is already loaded, so it can render without awaiting. */
export function loadedAdminPage(name: AdminPageName): AnyPage | undefined {
  return ready.get(name);
}

/** Load a page chunk through the release-aware loader (one guarded reload on a stale chunk). */
export async function loadAdminPage(name: AdminPageName): Promise<AnyPage> {
  const loader: () => Promise<PageModule> = loaders[name];
  const module = await loadPage(name, loader);
  ready.set(name, module.default);
  return module.default;
}

/** Warm a page chunk ahead of a likely navigation. Failures are left to the real load. */
export function preloadAdminPage(name: AdminPageName): void {
  if (ready.has(name)) return;
  loaders[name]()
    .then((module) => ready.set(name, module.default))
    .catch(() => {});
}
